import { ChevronIcon } from "@/Icons/v2/DatePickerIcons/Chevron";
import { useTerminalsState } from "@/States/Terminals";
import { timezoneStore } from "@/api/hooks/useConfig";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { type DateRange, DayPicker } from "react-day-picker";
import * as dayPickerLocales from "react-day-picker/locale";

import { Popover, PopoverPanel } from "@headlessui/react";
import { get } from "lodash";

import { DatePickerButton } from "./components/button";
import { DateInput } from "./components/dateInput";
import { DayButton } from "./components/dayButton";
import { PresetsView } from "./components/presetsView";
import { PresetKey } from "./constants";
import { dayPickerClassNames, dayPickerStyle, dayPickerVars } from "./styles";
import {
  fromIso,
  getPresetRange,
  toDisplayDate,
  toDisplayRange,
  toEndOfDay,
  toUtcRange,
} from "./utils";

type Props = {
  value?: DateRange;
  presetValue?: PresetKey;
  onChange: (range: DateRange, preset: PresetKey) => void;
};

export const DatePicker: React.FC<Props> = ({
  value,
  presetValue,
  onChange,
}: Props) => {
  const { language } = useTrans();
  const { timezone } = timezoneStore();
  const { currentTerminal } = useTerminalsState();
  const { terminalConfig, isLoadingTerminalConfig } = useConfigService();

  const isTerminalConfigResolved =
    !!currentTerminal?.id && !isLoadingTerminalConfig;

  const resolvedDefaultPreset =
    (terminalConfig?.defaultTimeFrame as PresetKey | undefined) ||
    PresetKey.Last30;

  const [preset, setPreset] = useState<PresetKey>(presetValue!);
  const [range, setRange] = useState<DateRange>(
    value
      ? toDisplayRange(value, timezone)
      : getPresetRange(resolvedDefaultPreset, timezone),
  );
  const [userSelected, setUserSelected] = useState(false);
  const [displayedMonth, setDisplayedMonth] = useState(range.to);

  useEffect(() => {
    setDisplayedMonth(range.to);
  }, [range]);

  useEffect(() => {
    if (!isTerminalConfigResolved || userSelected || value) return;

    const next = getPresetRange(resolvedDefaultPreset, timezone);
    setPreset(resolvedDefaultPreset);
    setRange(next);

    // update with generated initial value
    onChange(toUtcRange(next, timezone), resolvedDefaultPreset);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone, value, resolvedDefaultPreset, isTerminalConfigResolved]);

  const applyRange = useCallback(
    (next: DateRange, nextPreset: PresetKey) => {
      setUserSelected(true);
      setPreset(nextPreset);
      setRange(next);
      if (next.from && next.to) {
        onChange?.(toUtcRange(next, timezone), nextPreset);
      }
    },
    [onChange, timezone],
  );

  const selectPreset = useCallback(
    (key: PresetKey) => {
      applyRange(getPresetRange(key, timezone), key);
    },
    [applyRange, timezone],
  );

  const selectRange = useCallback(
    (next?: DateRange) => {
      if (!next) return;

      applyRange(
        { from: next.from, to: next.to ? toEndOfDay(next.to) : next.to },
        PresetKey.Custom,
      );
    },
    [applyRange],
  );

  const onTypeDate = useCallback(
    (which: "from" | "to", iso: string) => {
      if (!iso) return;

      let nextFrom = which === "from" ? fromIso(iso) : range.from;
      let nextTo = which === "to" ? fromIso(iso) : range.to;

      if (nextFrom && nextTo && nextFrom > nextTo) {
        if (which === "from") nextTo = nextFrom;
        else nextFrom = nextTo;
      }
      applyRange(
        { from: nextFrom, to: nextTo ? toEndOfDay(nextTo) : nextTo },
        PresetKey.Custom,
      );
    },
    [range, applyRange],
  );

  const today = useMemo(() => toDisplayDate(new Date(), timezone), [timezone]);

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <DatePickerButton
            preset={preset}
            range={range}
            open={open}
            isInitialising={!value && !userSelected}
          />
          <PopoverPanel
            anchor={{ to: "bottom end", gap: 4.5 }}
            className="border-grey3 z-[10000] w-[300px] rounded-lg border bg-white shadow-lg"
          >
            <div className="border-grey2 flex items-center gap-[6px] border-b px-[12px] pt-[10px] pb-[11px]">
              <DateInput
                labelKey="datePicker:startInputLabel"
                value={range.from}
                maxValue={today}
                onChange={(value) => onTypeDate("from", value)}
              />
              <span className="text-grey4 pt-[14px]">→</span>
              <DateInput
                labelKey="datePicker:endInputLabel"
                value={range.to}
                maxValue={today}
                onChange={(value) => onTypeDate("to", value)}
              />
            </div>

            <div className="flex justify-center pt-1.5 pb-1.5">
              <style>{dayPickerStyle}</style>
              <DayPicker
                mode="range"
                locale={get(dayPickerLocales, language, dayPickerLocales.enGB)}
                selected={range}
                weekStartsOn={1}
                onSelect={selectRange}
                resetOnSelect
                disabled={{ after: today }}
                required={false}
                navLayout="around"
                showOutsideDays
                fixedWeeks
                style={dayPickerVars}
                month={displayedMonth}
                onMonthChange={setDisplayedMonth}
                components={{ DayButton, Chevron: ChevronIcon }}
                formatters={{
                  formatWeekdayName: (date) =>
                    date.toLocaleDateString("en-US", { weekday: "narrow" }),
                }}
                classNames={dayPickerClassNames}
              />
            </div>

            <div className="bg-grey2 h-px" />

            <PresetsView selectedPreset={preset} selectPreset={selectPreset} />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
